import React from "react";
import { Box } from "@mui/material";
import dayjs from "dayjs";
import "./templateStyling.css";
import { useTranslation } from "react-i18next";
import {
  getDisruptionAssetSrc,
  getDirectionImage,
} from "../../helper/disruptionHelper";

/**
 * Component for showing Disruption or Affected Preview based on type and order
 * @param {Array} disruptions Disruption Data Array Object
 * @param {String} type Type of Disruption "DISRUPTION|AFFECTED"
 * @param {Int16Array} order Order of the disruption data shown
 */
const PreviewTemplate = ({ disruptions, type, order }) => {
  return (
    <Box
      sx={{
        border: "1vw solid #ffd342",
        width: "100%",
        height: "100%",
      }}
    >
      {type === "DISRUPTION" ? (
        <DisruptionTemplate data={disruptions[order]} />
      ) : type === "AFFECTED" ? (
        <AffectedTemplate data={disruptions[order]} />
      ) : (
        <StationClosureTemplate data={disruptions[order]} />
      )}
    </Box>
  );
};

/** Object Containing SMRT Line Color for background and Contrast Color for Text */
const lineColorConfig = {
  CAL: { backgroundColor: "#009c4b", color: "#000000" },
  CCL: { backgroundColor: "#f99c25", color: "#000000" },
  CCLE: { backgroundColor: "#f99c25", color: "#000000" },
  DTL: { backgroundColor: "#005bc0", color: "#ffffff" },
  EWL: { backgroundColor: "#009c4b", color: "#000000" },
  NEL: { backgroundColor: "#8e3494", color: "#ffffff" },
  NSL: { backgroundColor: "#e02725", color: "#ffffff" },
  TEL: { backgroundColor: "#bb5117", color: "#ffffff" },
  LRT: { backgroundColor: "#dcdcdc", color: "#000000" },
};

/** Generate Disruption Template Component */
const DisruptionTemplate = ({ data }) => {
  const { t } = useTranslation("smrtDisruption");
  return (
    <Box className="disruption__content-container">
      <Box className="disruption__service-affected_container">
        <Box className="disruption__top-text-container">
          {data.type === "delay"
            ? t("preview.template.longerTravelingTimeText")
            : t("preview.template.mrtAffectedText")}
        </Box>
        <Box
          className="disruption__line-text-container"
          sx={
            lineColorConfig[
              data.line.code.includes("LRT") ? "LRT" : data.line.code
            ]
          }
        >
          {data.line.name}
        </Box>
        {data.fromStation.abbreviation !== "ALL" &&
          data.toStation.abbreviation !== "ALL" && (
            <Box className="disruption__line-image-container">
              <img
                src={getDisruptionAssetSrc(
                  data.fromStation.abbreviation +
                    "-" +
                    data.fromStation.code +
                    "-L.png"
                )}
                alt={data.fromStation.name}
              />
              <img
                style={{ width: "24%" }}
                src={getDisruptionAssetSrc(
                  getDirectionImage(
                    data.direction,
                    data.type,
                    data.isAffectingAllStation
                  )
                )}
                alt={getDirectionImage(data.direction)}
              />
              <img
                src={getDisruptionAssetSrc(
                  data.toStation.abbreviation +
                    "-" +
                    data.toStation.code +
                    "-L.png"
                )}
                alt={data.toStation.name}
              />
            </Box>
          )}
      </Box>
      <Box className="disruption__time-container">
        {data.isMessageHidden ? null : (
          <>
            <Box className="disruption__time-info-text">
              {data.type === "disruption"
                ? t("preview.template.expectedResumeText")
                : t("preview.template.estimatedDelayText")}
            </Box>
            <Box className="disruption__time-text">
              {data.type === "disruption"
                ? dayjs(data.resumeAt).format("hh:mm A")
                : data.delayMinutes + " mins"}
            </Box>
          </>
        )}
      </Box>
      <Box className="bottom-text-container">
        {t("preview.template.apologizeBottomText")}
      </Box>
    </Box>
  );
};

/** Generate Affected Stations Template Component */
const AffectedTemplate = ({ data }) => {
  const { t } = useTranslation("smrtDisruption");
  return (
    <Box className="disruption__content-container">
      <Box className="affected__service-affected_container">
        <img
          className="no-service-img"
          src={getDisruptionAssetSrc("./no-service.png")}
          alt={"no-service"}
        />
        <Box className="affected__top-container">
          <Box className="affected__top-text-container">
            {t("preview.template.mrtAffectedText")}
          </Box>
          <Box
            className="affected__line-text-container"
            sx={lineColorConfig[data.line.code]}
          >
            {data.line.name}
          </Box>
        </Box>
      </Box>
      <Box className="affected__station-list">
        {data.affectedStations.map((station) => (
          <Box
            className="affected__station-list_item"
            key={station.abbreviation + "-" + station.code}
          >
            <img
              src={getDisruptionAssetSrc(
                station.abbreviation + "-" + station.code + "-L.png"
              )}
              alt={station.name}
            />
          </Box>
        ))}
      </Box>
      <Box className="bottom-text-container">
        {t("preview.template.apologizeBottomText")}
      </Box>
    </Box>
  );
};

/** Generate Affected Stations Template Component */
const StationClosureTemplate = ({ data }) => {
  const { t } = useTranslation("smrtDisruption");
  return (
    <Box className="disruption__content-container">
      <Box className="affected__service-affected_container">
        <Box className="affected__top-container">
          <Box className="affected__top-text-container">
            {t("preview.template.stationClosureText")}
          </Box>
          <Box
            className="affected__line-text-container"
            sx={lineColorConfig[data.line.code]}
          >
            {data.line.name}
          </Box>
        </Box>
      </Box>
      <Box className="affected__station-list">
        {data.affectedStations.map((station) => (
          <Box
            className="affected__station-list_item"
            key={station.abbreviation + "-" + station.code}
          >
            <img
              src={getDisruptionAssetSrc(
                station.abbreviation + "-" + station.code + "-L.png"
              )}
              alt={station.name}
            />
          </Box>
        ))}
      </Box>
      <Box className="bottom-text-container">
        {t("preview.template.apologizeBottomText")}
      </Box>
    </Box>
  );
};

export default PreviewTemplate;
